import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import {DefaultInitialPaginationInfo} from "../../../../core/constants/enums";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../packages/data-grid";
import Button from "../../base/button/button";
import SearchInput from "../../base/input/search_input";
import PartnerDataGrid from "../../../containers/data-grid";
import {AssignEntityCreditsToPartSupportDataGridColumnNames} from "../../../../core/constants/table/data-grid-column-keys";


const _initialPaginationInfo = {
    ...DefaultInitialPaginationInfo,
    currentPage: 1,
    pageSize: 5,
}

const AssignEntityCreditsToPartSupportTable = ({
                                                   dataGridSaveConfig,
                                                   search: searchProps,
                                                   connect: connectProps,
                                                   includePagination,
                                               }) => {
    const [loading, setLoading] = useState(true)
    const [credits, setCredits] = useState([])
    const [keyword, setKeyword] = useState('');
    const isMounted = useIsMounted();

    const columns = useMemo(() => [
        /** @type {DataGridColumn[]}*/
        {
            title: 'Number',
            name: AssignEntityCreditsToPartSupportDataGridColumnNames.number,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: 'Date',
            name: AssignEntityCreditsToPartSupportDataGridColumnNames.date,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: 'PO Number',
            name: AssignEntityCreditsToPartSupportDataGridColumnNames.poNumber,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: 'Total',
            name: AssignEntityCreditsToPartSupportDataGridColumnNames.total,
            alignment: DataGridColumnAlignments.right,
            type: DataGridColumnTypes.money,
            sortable: false,
        },
        {
            name: AssignEntityCreditsToPartSupportDataGridColumnNames.select,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.element,
            sortable: false,
        },
    ], [])

    const [dataGridColumns, _, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize && includePagination ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize, includePagination]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo)

    const anyCreditsConnecting = useMemo(() => credits?.some(e => e.connecting), [credits]);

    /**
     * Changes the selected keyword of the searching filter to trigger a searching api call.
     */
    const search = useCallback(async () => {
        setLoading(true);
        const response = await searchProps(keyword, {pageSize: paginationInfo.pageSize, currentPage: paginationInfo.currentPage});
        if (!isMounted() || !response)
            return;
        setCredits(response?.resultList?.map(e => ({
            ...e,
            connecting: false,
        })) ?? []);
        setPaginationInfo(prevState => ({...prevState, length: response?.paginationInfo?.length ?? 0}))
        setLoading(false);
    }, [isMounted, keyword, paginationInfo.currentPage, paginationInfo.pageSize, searchProps])

    /**
     * Listens for the changes in keywords, and paginationInfo, and with each change:
     * - if the dialog is open, searches for the entries.
     */
    useEffect(() => {
        setCredits([]);
        search().then();
    }, [search])

    /**
     * Connects the given credit to the specified part support request.
     * @param {number} creditId
     */
    const connect = useCallback(async (originalCredit) => {
        setCredits(prevState => prevState?.map(e => e.id === originalCredit.id ? {...e, connecting: true} : e) ?? [])
        await connectProps(originalCredit);
        if (!isMounted()) return;
        setCredits(prevState => prevState?.map(e => e.id === originalCredit.id ? {
            ...e,
            connecting: false
        } : e) ?? [])
    }, [connectProps, isMounted])

    /**
     * Sets the [keywords] and [pagination.currentPage] values as soon as the search-input calls this callback.
     * @param keywords
     */
    const onKeywordsChanged = (keywords) => {
        setKeyword(keywords ?? '');
        setPaginationInfo(prevState => ({...prevState, currentPage: 1}))
    }

    /**
     * @type {DataGridRow[]}
     */
    const dataGridRows = useMemo(() => {
        return credits.map(credit => {
            return {
                key: credit.id,
                data: credit,
                cells: {
                    [AssignEntityCreditsToPartSupportDataGridColumnNames.number]: credit.number,
                    [AssignEntityCreditsToPartSupportDataGridColumnNames.date]: credit.date,
                    [AssignEntityCreditsToPartSupportDataGridColumnNames.poNumber]: credit.poNumber,
                    [AssignEntityCreditsToPartSupportDataGridColumnNames.total]: credit.total,
                    [AssignEntityCreditsToPartSupportDataGridColumnNames.select]:
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => connect(credit.originalData)}
                            disabled={loading || anyCreditsConnecting}
                        >
                            {!!credit?.connecting ? "Connecting..." : "Select"}
                        </Button>,
                }
            }
        })
    }, [anyCreditsConnecting, connect, credits, loading])

    return (
        <>
            <SearchInput
                loading={loading}
                autoSearch
                search={onKeywordsChanged}
                label={'Search'}
                variant={"outlined"}
                paperProps={{className: 'px-0'}}
            />
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                classNames={{
                    container: 'px-0',
                }}
                rows={dataGridRows}
                columns={dataGridColumns}
                hideExporting
                hideFooterActions
                hidePagination={!includePagination}
                visibleRows={5}
                state={{
                    loading: {state: loading},
                    pagination: paginationInfo,
                    density: dataGridDensity,
                }}
                onPageSizeChanged={(_, newVal) => setPaginationInfo(newVal)}
                onCurrentPageChanged={(_, newVal) => setPaginationInfo(newVal)}
            />
        </>
    )
}

export default AssignEntityCreditsToPartSupportTable;
